import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import CheckInOutEventViewModel from '@app/models/check-in-out-event-view-model';
import CheckInOutSearchRequest from '@app/models/check-in-out-search-request';
import CheckInOutViewModel from '@app/models/check-in-out-view-model';
import PagedResults from '@app/models/paged-results';
import { DateService, LogService, UserService } from '@app/services';
import { ClickContext } from '@app/utilities/click-context';
import { LinkToBookingDetailComponent } from '../../../common-components/link-to-booking-detail/link-to-booking-detail.component';
import { LinkToUserAccountDetailComponent } from '../../../common-components/link-to-user-account-detail/link-to-user-account-detail.component';
import { LinkToNewAdminListingComponent } from '../link-to-new-admin-listing/link-to-new-admin-listing.component';
import { DatePipe } from '@angular/common';

@Component({
    selector: "check-in-event-grid",
    templateUrl: "./check-in-event-grid.component.html",
    styleUrls: ["./check-in-event-grid.component.less"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [LinkToNewAdminListingComponent, LinkToUserAccountDetailComponent, LinkToBookingDetailComponent, DatePipe]
})
export class CheckInEventGridComponent implements OnInit {
  private readonly _componentName = 'CheckInEventGridComponent';

  private _results: PagedResults;

  public rows: CheckInOutEventViewModel[];

  /**
   * Click context to manage single vs double click on the task row
   */
  private clickContext = new ClickContext();

  /**
   * Flag that toggles visibility of all columns vs a set of regular columns visible to users
   */
  public showAllColumns: boolean;

  public sort = 'createdOn';

  public sortDescending = true;

  /**
   * Flag that is set to true while grid data is being retrieved from the API
   */
  @Input()
  public retrieving = false;

  @Output()
  public rowClicked = new EventEmitter<CheckInOutViewModel>();

  @Output()
  public rowDoubleClicked = new EventEmitter<CheckInOutViewModel>();

  /**
   * Event that is raised when the data is invalidated and needs to be refreshed
   */
  @Output()
  public invalidated = new EventEmitter<CheckInOutSearchRequest>();

  public offset: number;

  public showEmailColumn: boolean;

  public showComments: boolean;

  constructor(private log: LogService, private dateService: DateService, private userService: UserService, private router: Router) {
    this.offset = new Date().getTimezoneOffset();
  }

  ngOnInit() {}

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    // return true;
    return this.log.verboseLogging;
  }

  public get results(): PagedResults {
    return this._results;
  }

  public get isAdmin(): boolean {
    return this.userService.isAdmin;
  }

  public get isSuperAdmin(): boolean {
    return this.userService.isSuperAdmin;
  }

  @Input()
  public set results(v: PagedResults) {
    this._results = v;

    if (v && v.results) {
      this.rows = v.results.map((x) => x as CheckInOutEventViewModel);

      this.rows.forEach((x) => (x.timestamp = this.dateService.parseUTCDateTime(String(x.timestamp))));
    } else {
      this.rows = [];
    }

    if (this.verboseLogging) {
      console.log(`${this._componentName}.results - set - rows=`, this.rows);
    }
  }

  public onSetSortClick(sortColumnName: string) {
    if (sortColumnName != this.sort) {
      this.sort = sortColumnName;

      console.log(`${this._componentName}onSetSortClick  sort=`, this.sort);

      // parcelGridRows
    } else {
      this.sortDescending = !this.sortDescending;
    }

    this.retrieveData();
  }

  public onSingleClick = this.clickContext.clickHandler((evt: MouseEvent, row: CheckInOutViewModel) => {
    this.onRowClick(evt, row);
  });

  public onDoubleClick = this.clickContext.dblClickHandler((evt: MouseEvent, row: CheckInOutViewModel) => {
    this.onRowDoubleClick(evt, row);
  });

  private retrieveData() {
    let request = {
      listingId: null,
      sortCol: this.sort,
      sortDirection: this.sortDescending ? 'desc' : 'asc',
    } as CheckInOutSearchRequest;

    this.invalidated.next(request);
  }

  public onRowClick(evt: MouseEvent, row: CheckInOutViewModel) {
    console.log(`${this._componentName}.onRowClick row=`, row);

    this.rowClicked.emit(row);
  }

  public onRowDoubleClick(evt: MouseEvent, row: CheckInOutViewModel) {
    console.log(`${this._componentName}.onRowDoubleClick row=`, row);

    this.rowDoubleClicked.emit(row);
  }

  public onManualCheckOut(row: CheckInOutViewModel) {
    this.router.navigate(['/admin/booking', row.bookingId, 'check-out']);
  }

  public onManualCheckIn(row: CheckInOutViewModel) {
    this.router.navigate(['/admin/booking', row.bookingId, 'check-in']);
  }
}
